import React, { createContext, useContext, useEffect, useState } from 'react';
import { Auth, onAuthStateChanged, User } from 'firebase/auth';

interface AuthContextProps {
  currentUser: User | null;
  auth: Auth;
  logout: () => Promise<void>;
  roles?: string[];
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ auth: Auth, children: React.ReactNode }> = ({ auth, children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(auth.currentUser);
  const [roles, setRoles] = useState<string[] | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          const idTokenResult = await user.getIdTokenResult();
          const claimRoles = idTokenResult.claims?.roles;
          // roles claim is expected to be a string array; normalize to string[]
          if (Array.isArray(claimRoles)) {
            setRoles(claimRoles.map(String));
          } else if (typeof claimRoles === 'string') {
            setRoles([claimRoles]);
          } else {
            setRoles([]);
          }
        } catch (e) {
          setRoles([]);
        }
      } else {
        setRoles(undefined);
      }
    });

    return () => unsubscribe();
  }, [auth]);

  const logout = async () => {
    await auth.signOut();
  };

  const isAdmin = (roles ?? []).includes('admin');

  return (
    <AuthContext.Provider value={{ currentUser, auth, logout, roles, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
